import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { businessInfo } from '../data/mockData';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventType: '',
    guestCount: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Prepare data for API
      const inquiryData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        event_date: formData.eventDate || null,
        event_type: formData.eventType || null,
        guest_count: formData.guestCount || null,
        message: formData.message
      };

      // Submit to backend API
      await axios.post(`${BACKEND_URL}/api/inquiries/`, inquiryData);
      
      toast.success('Inquiry submitted successfully! We\'ll contact you soon.', {
        duration: 5000,
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        eventDate: '',
        eventType: '',
        guestCount: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again or email us directly.', {
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 bg-gradient-to-b from-yellow-50 via-orange-50 to-white overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-1/3 h-1/2 bg-gradient-to-bl from-orange-200/40 to-yellow-200/40 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/3 bg-gradient-to-tr from-emerald-200/30 to-green-200/30 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-orange-600 font-bold text-sm uppercase tracking-wider bg-gradient-to-r from-orange-100 to-yellow-100 px-4 py-2 rounded-full border border-orange-200">
            Get in Touch
          </span>
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-burgundy-800 via-red-700 to-orange-700 bg-clip-text text-transparent mt-6 mb-4">
            Book Your Event
          </h2>
          <p className="text-gray-700 text-lg max-w-2xl mx-auto">
            Ready to bring Desi Fusion to your next event? Fill out the form below and we'll get back to you shortly
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Contact Info */}
          <div className="space-y-8">
            <div className="bg-gradient-to-br from-burgundy-900 via-red-900 to-burgundy-800 rounded-3xl p-10 text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/20 rounded-full blur-2xl"></div>
              
              <div className="relative z-10">
                <h3 className="text-3xl font-bold mb-6">Contact Information</h3>
                <p className="text-orange-100 mb-8 leading-relaxed">
                  Have questions? Reach out to us directly, and let's discuss how we can make your event special.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Mail size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-orange-200 mb-1">Email</p>
                      <a href={`mailto:${businessInfo.email}`} className="text-lg hover:text-orange-300 transition-colors">
                        {businessInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Phone size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-orange-200 mb-1">Phone</p>
                      <p className="text-lg">{businessInfo.phone}</p>
                      <p className="text-xs text-orange-200 mt-1">(Please contact us to get our current number)</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-orange-200 mb-1">Service Area</p>
                      <p className="text-lg">Mobile Service</p>
                      <p className="text-sm text-orange-100 mt-1">Available for events across the region</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Operating Hours */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-orange-100">
              <h4 className="text-xl font-bold bg-gradient-to-r from-burgundy-800 to-red-700 bg-clip-text text-transparent mb-4">Operating Hours</h4>
              <p className="text-gray-600">
                <span className="font-semibold">Flexible scheduling</span> based on your event needs. We operate at various times to accommodate weddings, corporate events, festivals, and private bookings.
              </p>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-white rounded-3xl p-10 shadow-2xl border-2 border-orange-100">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-navy-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-navy-900 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-navy-900 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                    placeholder="+44 7XXX XXXXXX"
                  />
                </div>

                <div>
                  <label htmlFor="eventDate" className="block text-sm font-semibold text-navy-900 mb-2">
                    Event Date
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="eventType" className="block text-sm font-semibold text-navy-900 mb-2">
                    Event Type
                  </label>
                  <select
                    id="eventType"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                  >
                    <option value="">Select type</option>
                    <option value="wedding">Wedding</option>
                    <option value="corporate">Corporate Event</option>
                    <option value="birthday">Birthday Party</option>
                    <option value="festival">Festival/Fair</option>
                    <option value="private">Private Gathering</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="guestCount" className="block text-sm font-semibold text-navy-900 mb-2">
                    Guest Count
                  </label>
                  <input
                    type="number"
                    id="guestCount"
                    name="guestCount"
                    value={formData.guestCount}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                    placeholder="50"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-navy-900 mb-2">
                  Additional Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all resize-none"
                  placeholder="Tell us about your event, location, special requirements, etc."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-orange-500 via-orange-600 to-yellow-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-orange-600 hover:via-orange-700 hover:to-yellow-600 transition-all duration-300 hover:scale-[1.02] shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{submitting ? 'Sending...' : 'Send Inquiry'}</span>
                {!submitting && <Send size={20} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
